import * as React from "react";
import {
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useState, useEffect } from "react";
import { i18n } from "../../../translate/i18n";

/*
  selecteds: [{ connectionId, queueIds: [singleQueueId?] }]
*/

const normalizeSelectionList = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => ({
      connectionId: Number(item.connectionId),
      queueId: item.queueIds?.[0] ? Number(item.queueIds[0]) : null,
    }))
    .filter((item) => !Number.isNaN(item.connectionId) && item.connectionId > 0)
    .sort((a, b) => a.connectionId - b.connectionId);
};

export const isConnectedConnection = (conn) =>
  String(conn?.status || "").toLowerCase() === "connected";

export const areConnectionQueueSelectionsEqual = (a, b) => {
  const left = normalizeSelectionList(a);
  const right = normalizeSelectionList(b);
  if (left.length !== right.length) return false;
  return left.every(
    (item, index) =>
      item.connectionId === right[index].connectionId &&
      item.queueId === right[index].queueId
  );
};

const ApiConnectionQueueConfiguration = ({
  selecteds = [],
  onSelected,
  connections = [],
  isCollapsed = true,
}) => {
  const [selectedConnectionOptions, setSelectedConnectionOptions] = useState([]);
  const [collapsedConnections, setCollapsedConnections] = useState([]);

  const mapSelectedsFromProps = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .filter((item) => connections.some((c) => c.id == item.connectionId))
      .map((item) => ({
        connectionId: item.connectionId,
        queueIds: Array.isArray(item.queueIds) ? item.queueIds.slice(0, 1) : [],
      }));
  };

  useEffect(() => {
    const mapped =
      selecteds && Array.isArray(selecteds) && selecteds.length > 0
        ? mapSelectedsFromProps(selecteds)
        : [];

    setSelectedConnectionOptions((prev) =>
      areConnectionQueueSelectionsEqual(prev, mapped) ? prev : mapped
    );
  }, [selecteds, connections]);

  useEffect(() => {
    if (Array.isArray(connections) && connections.length > 0 && isCollapsed) {
      setCollapsedConnections(connections.map((c) => c.id));
    }
  }, [connections, isCollapsed]);

  const handleCollapsed = (connId) => {
    setCollapsedConnections((prev) => {
      const alreadyExist = prev.includes(connId);
      if (alreadyExist) return prev.filter((cId) => cId !== connId);
      return [...prev, connId];
    });
  };

  const connectionIsSelected = (connId) =>
    selectedConnectionOptions.some((c) => c.connectionId == connId);

  const commitSelection = (prev, next) => {
    if (areConnectionQueueSelectionsEqual(prev, next)) return prev;
    onSelected(next);
    return next;
  };

  const handleConnectionSelection = (connId) => {
    if (!connId) return;

    const conn = connections.find((c) => c.id == connId);
    if (!conn || !isConnectedConnection(conn)) return;

    setSelectedConnectionOptions((prev) => {
      const connSelectedIdx = prev.findIndex((sc) => sc.connectionId == connId);
      const next =
        connSelectedIdx !== -1
          ? prev.filter((c) => c.connectionId != connId)
          : [...prev, { connectionId: connId, queueIds: [] }];
      return commitSelection(prev, next);
    });
  };

  const handleQueueSelection = (connectionId, queueId) => {
    if (!connectionId || !queueId) return;

    setSelectedConnectionOptions((prev) => {
      const index = prev.findIndex((option) => option.connectionId === connectionId);
      const current = index !== -1 ? prev[index] : null;
      const alreadySelected = current?.queueIds?.[0] == queueId;

      const nextEntry = {
        connectionId,
        queueIds: alreadySelected ? [] : [queueId],
      };

      let next;
      if (index !== -1) {
        next = [...prev];
        next[index] = nextEntry;
      } else {
        next = [...prev, nextEntry];
      }

      return commitSelection(prev, next);
    });
  };

  const queueIsSelected = (connId, queueId) => {
    const selectedConnection = selectedConnectionOptions.find(
      (c) => c.connectionId == connId
    );
    return selectedConnection?.queueIds?.[0] == queueId;
  };

  const getConnectionLabel = (conn) => {
    const isMassive = String(conn?.option || "").toLowerCase() === "massive";
    const isConnected = isConnectedConnection(conn);
    const number = conn.numberConnection || "—";

    if (!isMassive) {
      return `${conn.name} - ${i18n.t("integrations.api.form.connectionAttendanceOnly")}`;
    }
    if (!isConnected) {
      return `${conn.name} (${number}) - ${i18n.t("integrations.api.form.connectionDisconnected")}`;
    }
    return `${conn.name} (${number})`;
  };

  if (!Array.isArray(connections) || connections.length === 0) {
    return (
      <Typography variant="body2" color="error" component="p">
        {i18n.t("integrations.api.form.noMassiveConnections")}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table aria-label="api-connection-queue-table">
        <TableBody>
          {connections
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((conn) => (
              <React.Fragment key={conn.id}>
                <TableRow>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={connectionIsSelected(conn.id)}
                      disabled={!isConnectedConnection(conn)}
                      onChange={() => handleConnectionSelection(conn.id)}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row" align="left">
                    {getConnectionLabel(conn)}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleCollapsed(conn.id)}
                      disabled={!Array.isArray(conn?.Queues) || conn.Queues.length === 0}
                    >
                      {collapsedConnections.includes(conn.id) ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
                {Array.isArray(conn?.Queues) && conn.Queues.length > 0 && (
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                      <Collapse
                        in={!collapsedConnections.includes(conn.id)}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                {i18n.t("integrations.api.form.connectionQueueTable.queue")}
                              </TableCell>
                              <TableCell>
                                {i18n.t("integrations.api.form.connectionQueueTable.linkQueue")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {conn.Queues.map((queue) => (
                              <TableRow key={`${conn.id}-${queue.id}`}>
                                <TableCell>{queue.name}</TableCell>
                                <TableCell>
                                  <Checkbox
                                    size="small"
                                    color="primary"
                                    disabled={!connectionIsSelected(conn.id)}
                                    checked={queueIsSelected(conn.id, queue.id)}
                                    onChange={() =>
                                      handleQueueSelection(conn.id, queue.id)
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ApiConnectionQueueConfiguration;
